import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';
import AnimatedSection from '../components/AnimatedSection';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrandsHub = () => {
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get(`${API}/brands`);
        setBrands(response.data);
      } catch (error) {
        console.error('Error fetching brands:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBrands();
  }, []);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-ewss-navy via-ewss-navy to-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our <span className="text-ewss-cyan">Brands</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Premium automation components from industry-leading manufacturers
            </p>
          </motion.div>
        </div>
      </section>

      {/* Brands Grid */}
      <section className="py-20 bg-ewss-navy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="text-center text-gray-400">Loading brands...</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {brands.map((brand, index) => {
                const borderColor =
                  brand.color === '#00E5FF' || brand.color === '#1791FF' ? 'border-ewss-cyan' :
                  brand.color === '#EF4444' ? 'border-ewss-red' :
                  brand.color === '#10B981' ? 'border-ewss-green' :
                  'border-ewss-orange';
                const hoverShadow =
                  brand.color === '#00E5FF' || brand.color === '#1791FF' ? 'hover:shadow-[0_0_30px_rgba(0,229,255,0.3)]' :
                  brand.color === '#EF4444' ? 'hover:shadow-[0_0_30px_rgba(239,68,68,0.3)]' :
                  brand.color === '#10B981' ? 'hover:shadow-[0_0_30px_rgba(16,185,129,0.3)]' :
                  'hover:shadow-[0_0_30px_rgba(249,115,22,0.3)]';
                const textColor =
                  brand.color === '#00E5FF' || brand.color === '#1791FF' ? 'text-ewss-cyan' :
                  brand.color === '#EF4444' ? 'text-ewss-red' :
                  brand.color === '#10B981' ? 'text-ewss-green' :
                  'text-ewss-orange';

                return (
                  <AnimatedSection key={brand.id} delay={index * 0.1}>
                    <Link to={`/brands/${brand.slug}`} className="group block">
                      <div className={`relative overflow-hidden rounded-xl bg-gradient-to-br from-white/5 to-transparent border ${borderColor}/30 hover:${borderColor} transition-all duration-300 ${hoverShadow} p-6 h-full`}>
                        <div className="aspect-square mb-6 overflow-hidden rounded-lg bg-black/50">
                          <img 
                            src={brand.image_url}
                            alt={brand.name}
                            className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="text-center mb-4">
                          <div className={`text-3xl font-bold ${textColor} mb-2`}>{brand.logo_placeholder}</div>
                          <p className="text-gray-400 text-sm">{brand.tagline}</p>
                        </div>
                        <p className="text-gray-300 text-sm mb-4">{brand.description}</p>
                        <div className="flex flex-wrap gap-2 mb-4">
                          {brand.categories.slice(0, 3).map((category, idx) => (
                            <span key={idx} className="px-3 py-1 bg-white/5 text-gray-400 text-xs rounded-full border border-white/10">
                              {category}
                            </span>
                          ))}
                        </div>
                        <div className={`flex items-center justify-center ${textColor} group-hover:translate-x-2 transition-transform`}>
                          <span className="text-sm font-semibold">Learn More</span>
                          <ArrowRight size={16} className="ml-2" />
                        </div>
                      </div>
                    </Link>
                  </AnimatedSection>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BrandsHub;